import { Navigate, useLocation, useSearchParams } from "react-router"
import { Spinner } from "../../components/Spinner"

import GovtWizardContentIntroduction from "./GovtWizardContentIntroduction"
import { useAccessToken } from "../../stores"
import { useGovernmentStore } from "../../stores/useGovernmentStore"
import GovtWizardContentQuiz from "./GovtWizardContentQuiz"
import GovtWizardContentSelectApproach from "./GovtWizardContentSelectApproach"
import GovtWizardContentTakeAction from "./GovtWizardContentTakeAction"
import GovtWizardContentPlanSummary from "./GovtWizardContentPlanSummary"
import { AccountTypes } from "@/types/account"
import { GovtApWizardType } from "@/types/govtAppWizard"
import { Profile } from "@/types"
import { ProjectListTypes } from "@/types/program"

export interface GovtWizardContentProps {
  account: AccountTypes | undefined
  profile: Profile | undefined
  projectList: ProjectListTypes | undefined
  govtAppWizard: GovtApWizardType | undefined
  isLoading: boolean
}

const GovtWizardContent = ({
  account,
  profile,
  projectList,
  govtAppWizard,
  isLoading = false,
}: GovtWizardContentProps) => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const accessToken = useAccessToken()
  const { enabledSteps, setEnabledSteps } = useGovernmentStore()

  const step = searchParams.get("step")

  const isLoggedIn = !!accessToken

  const handleNext = () => {
    const newActiveStep = Number(step) + 1
    const newEnabledSteps = [...enabledSteps]
    newEnabledSteps[Number(step)] = true
    searchParams.set("step", String(newActiveStep))

    setEnabledSteps(newEnabledSteps)
    setSearchParams(searchParams)

    window.scrollTo({
      top: 0,
      behavior: "instant",
    })
  }

  const handlePrevious = () => {
    searchParams.set("step", String(Number(step) - 1))

    setSearchParams(searchParams)
  }

  if (!isLoggedIn) {
    return <Navigate replace to="/getting-started" state={{ from: location }} />
  }

  if (isLoading) {
    return (
      <div className="absolute inset-0 top-[66px] flex items-center justify-center pointer-events-none">
        <Spinner />
      </div>
    )
  }

  if (step === "1") {
    return <GovtWizardContentIntroduction handleNext={handleNext} />
  }

  if (step === "2") {
    return (
      <GovtWizardContentQuiz
        govtAppWizard={govtAppWizard}
        handleNext={handleNext}
      />
    )
  }

  if (step === "3") {
    return (
      <GovtWizardContentSelectApproach
        govtAppWizard={govtAppWizard}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
      />
    )
  }

  if (step === "4") {
    return (
      <GovtWizardContentTakeAction
        account={account}
        profile={profile}
        govtAppWizard={govtAppWizard}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
      />
    )
  }

  if (step === "5") {
    return (
      <GovtWizardContentPlanSummary
        account={account}
        govtAppWizard={govtAppWizard}
        projectList={projectList}
      />
    )
  }

  return null
}

export default GovtWizardContent
